import {
  Accordion, AccordionDetails, AccordionSummary,
  Autocomplete,
  Box,
  Button, Checkbox,
  FormControl, FormControlLabel, FormGroup,
  InputLabel,
  LinearProgress, MenuItem, Radio, Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography, useTheme
} from "@mui/material";
import {
  AttackOriginLocation,
  AttackOriginLocationVertical,
  AttackOutcome,
  AttackOutLocation,
  AttackType,
  isAttackWithAttackType,
  isAttackWithOutLocation,
  originLocationMatches,
  PassType,

} from "sandy-shared/dist/types/attack-info.type";
import {
  ServeInPlayLocation,
  serveInPlayLocationMatches,
  ServeOutLocation,
  ServePlayerRelativeLocation,
  ServiceOutcome
} from "sandy-shared/dist/types/servce-info.type";
import React, {useEffect} from "react";
import './AnalysisPage.css';
import {PlayerInfoDto} from "sandy-shared/dist/dtos/player.dto";
import {getPlayers} from "../../services/player.service";
import {enqueueSnackbar} from "notistack";
import {StattedPointDto} from "sandy-shared/dist/dtos/statted-point.dto";
import {getAllPoints, getPlayerPoints, getPoints} from "../../services/point.service";
import {CourtSide} from "sandy-shared/dist/types/court-info.type";
import {getPartnerships, getPlayerPartnerships} from "../../services/partnership.service";
import DetailedStatsCard from "../../components/DetailedStatsCard";
import {
  getAttacksByDestinationLocation,
 getAttacksToDestinationLocation,
 getDetailedStatTotals,
  getPointsBreakdown,
  getPointsByAttackOriginLocation,
  getPointsByServeLocation, partnershipParticipatedInPoint, playerInPartnership
} from "sandy-shared/dist/analysis/analysis-helpers";
import {getServeInPlayLocation, getAttackDestinationLocation, getAttackOriginLocationHorizontal} from "../../shared/utility/shared-court-actions";
import Court, {CourtProps} from "../../components/Court";
import {PartnershipDto} from "sandy-shared/dist/dtos/partnership.dto";
import {getGradientBackgroundColor} from "./analysis-page-helpers";
import {DetailedStatTotals, emptyPointsBreakdown, PointsBreakdown} from "sandy-shared/dist/types/analysis-info.type";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {getMatches} from "../../services/match.service";
import {getAllMatchPlayerIds, getMatchCurrentPlayerIds} from "../../shared/utility/analysis-helpers";
import {Season} from "sandy-shared/dist/types/season-info.type";
import { MatchInfoDto, MatchType } from "sandy-shared/dist/dtos/match-info.dto";

const sectionSelectedColor = '#1565c0';
const sandActiveColor = '#c2b280';

const allMatchTypes: MatchType[] = ['Tournament', 'Scrimmage', 'Practice', 'Other'];

export interface EnrichedPointDto extends StattedPointDto {
  matchInfo: MatchInfoDto;
}

export const AnalysisPage = () => {
  const theme = useTheme();

  const allSeasons: Season[] = Object.values(Season) // TODO: This shouldn't be hardcoded
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [allPlayers, setAllPlayers] = React.useState<PlayerInfoDto[]>([]);
  const [allPartnerships, setAllPartnerships] = React.useState<PartnershipDto[]>([]);
  const [allPlayersWithPartnershipIds, setAllPlayersWithPartnershipIds] = React.useState<{playerId: number, partnershipIds: number[]}[]>([]);
  const [allPoints, setAllPoints] = React.useState<StattedPointDto[]>([]);
  const [allMatches, setAllMatches] = React.useState<MatchInfoDto[]>([]);
  const [schools, setSchools] = React.useState<string[]>([]);

  const [selectedSchool, setSelectedSchool] = React.useState<string | null>(null);
  const [selectedSchoolPoints, setSelectedSchoolPoints] = React.useState<EnrichedPointDto[]>([]);
  const [selectedSchoolPlayers, setSelectedSchoolPlayers] = React.useState<PlayerInfoDto[]>([]);
  const [selectedSeason, setSelectedSeason] = React.useState<Season>(allSeasons[allSeasons.length - 1]);

  const [selectedPlayers, setSelectedPlayers] = React.useState<PlayerInfoDto[]>([]);
  const [selectedMatchTypes, setSelectedMatchTypes] = React.useState<MatchType[]>(allMatchTypes);
  const [selectedMatches, setSelectedMatches] = React.useState<MatchInfoDto[]>([]);
  const [availableMatches, setAvailableMatches] = React.useState<MatchInfoDto[]>([]);

  const [selectedPoints, setSelectedPoints] = React.useState<EnrichedPointDto[]>([]);

  const [selectedSectionsPoints, setSelectedSectionsPoints] = React.useState<StattedPointDto[]>([]);
  const [pointsByServeLocation, setPointsByServeLocation] = React.useState<{location: ServeInPlayLocation, points: StattedPointDto[]}[]>([]);
  const [pointsByAttackOriginLocation, setPointsByAttackOriginLocation] = React.useState<{location: AttackOriginLocation, points: StattedPointDto[]}[]>([]);
  const [analysisType, setAnalysisType] = React.useState<'AttackLocation' | 'ServeLocation'>('AttackLocation');
  const [gradientType, setGradientType] = React.useState<'Volume' | 'Efficiency' | 'Off'>('Volume');
  const [passTypeFilter, setPassTypeFilter] = React.useState<PassType | 'All'>('All');
  const [selectedAttackOriginLocations, setSelectedAttackOriginLocations] = React.useState<AttackOriginLocation[]>([]);
  const [selectedServeLocations, setSelectedServeLocations] = React.useState<ServeInPlayLocation[]>([]);
  const [selectedPointsBreakdown, setSelectedPointsBreakdown] = React.useState<PointsBreakdown>(emptyPointsBreakdown);
  const [detailedPlayerStats, setDetailedPlayerStats] = React.useState<DetailedStatTotals| null>(null);

  const [expandedAccordion, setExpandedAccordion] = React.useState<'season' | 'players' | 'matchType' | 'matches' | null>(null);

  const selectedPlayersWithPartnershipIds = React.useMemo(() => {
    return allPlayersWithPartnershipIds.filter(p => selectedPlayers.find(sp => sp.id === p.playerId));
  }, [allPlayersWithPartnershipIds, selectedPlayers]);

  useEffect(() => {
    Promise.all([
      getAllPoints(selectedSeason),
      getMatches(),
      getPartnerships(),
      getPlayers()
    ]).then(([points, matches, partnerships, players]) => {
      setAllPoints(points);
      setAllMatches(matches);
      setAllPartnerships(partnerships);
      setAllPlayers(players);
      const allPlayersWithPartnershipIds = players.map(p => ({playerId: p.id, partnershipIds: partnerships.filter(partnership => playerInPartnership(p.id, partnership)).map(p => p.id)}));
      setAllPlayersWithPartnershipIds(allPlayersWithPartnershipIds);
      if(players.length > 0) {
        const schools = Array.from(new Set(players.map(p => p.school))).sort((a, b) => a.localeCompare(b));
        setSchools(schools);
        setSelectedSchool(prevSchool => prevSchool ?? schools[0]);
      }
    })
    .catch(e => {
      console.error('Error getting data', e);
      enqueueSnackbar(`Error getting data: ${e}`, {variant: 'error', autoHideDuration: 5000});
    })
    .finally(() => setIsLoading(false));
  }, [selectedSeason]);

  useEffect(() => {
    const schoolPlayers = allPlayers.filter(p => p.school === selectedSchool).sort((a, b) => a.lastName.localeCompare(b.lastName));
    const partnerships = allPartnerships.filter(p => schoolPlayers.find(sp => playerInPartnership(sp.id, p)));
    const rawPoints = allPoints.filter(p => partnerships.find(partnership => partnershipParticipatedInPoint(partnership.id, p)));
    const points = enrichPoints(rawPoints, allMatches);
    setSelectedSchoolPlayers(schoolPlayers);
    setSelectedSchoolPoints(points);

    setSelectedPlayers(schoolPlayers);
    setSelectedMatches(allMatches);
    setSelectedPoints(points);
  }, [selectedSchool, allPlayers, allPartnerships, allPoints, allMatches]);

  useEffect(() => {
    if(!selectedPlayers || !selectedMatchTypes) return;
    const playerIds = selectedPlayers.map(p => p.id);
    const relevantPartnershipIds = allPartnerships.filter(partnership => playerIds.find(playerId => playerInPartnership(playerId, partnership))).map(p => p.id);
    const relevantPoints = selectedSchoolPoints.filter(point =>
      selectedMatchTypes.includes(point.matchInfo.type)
      && relevantPartnershipIds.find(partnershipId => partnershipParticipatedInPoint(partnershipId, point))
      && (passTypeFilter === 'All' || (point.attackInfo?.passType === passTypeFilter))
      && selectedMatches.find(m => m.id === point.matchInfo.id));
    setSelectedPoints(relevantPoints);
  }, [selectedPlayers, selectedMatchTypes, selectedMatches, passTypeFilter]);

  useEffect(() => {
    if(!selectedPoints || !selectedPlayers || !selectedSchool) return;
    console.log(`Points related to ${selectedSchool} (${selectedPlayers.length} players): ${selectedPoints.length}`);
    const pointsByServeLocation = getPointsByServeLocation(selectedPoints, selectedPlayers.map(p => p.id));
    const pointsByAttackOriginLocation = getPointsByAttackOriginLocation(selectedPoints, selectedPlayers.map(p => p.id));
    setPointsByServeLocation(pointsByServeLocation);
    setPointsByAttackOriginLocation(pointsByAttackOriginLocation);
  }, [selectedPoints, selectedPlayers]);

  useEffect(() => {
    let newSelectedSectionsPoints: StattedPointDto[] = [];
    if(analysisType === 'ServeLocation') {
      if(selectedServeLocations.length === 0) {
        setSelectedSectionsPoints(selectedPoints); // When no sections are selected, show all points
        return;
      }
      newSelectedSectionsPoints = pointsByServeLocation
        .filter(p => !!selectedServeLocations.find(sl => serveInPlayLocationMatches(sl, p.location)))
        .flatMap(p => p.points);
    } else if(analysisType === 'AttackLocation') {
      if(selectedAttackOriginLocations.length === 0) {
        setSelectedSectionsPoints(selectedPoints); // When no sections are selected, show all points
        return;
      }
      newSelectedSectionsPoints = pointsByAttackOriginLocation
        .filter(p => !!selectedAttackOriginLocations.find(sal => originLocationMatches(sal, p.location)))
        .flatMap(p => p.points);
    }
    console.log(`Selected sections points: ${newSelectedSectionsPoints.length}`);
    setSelectedSectionsPoints(newSelectedSectionsPoints);
  }, [pointsByAttackOriginLocation, selectedAttackOriginLocations, pointsByServeLocation, selectedServeLocations, analysisType]);

  useEffect(() => {
    if(!selectedSchool || !selectedSectionsPoints) return;
    const pointsBreakdowns = getPointsBreakdown(selectedSectionsPoints, selectedPlayersWithPartnershipIds);
    setSelectedPointsBreakdown(pointsBreakdowns)
  }, [selectedSectionsPoints, selectedPlayers]);

  useEffect(() => {
    const detailedStats = getDetailedStatTotals(selectedPointsBreakdown);
    setDetailedPlayerStats(detailedStats);
  }, [selectedPointsBreakdown]);

  useEffect(() => {
    if(!selectedPlayers || !selectedMatchTypes || allMatches.length === 0 || allPoints.length === 0 || allPartnerships.length === 0) return;

    const availableMatches = allMatches.filter(m => {
      const allMatchPlayerIds = getAllMatchPlayerIds(m, allPoints, allPartnerships);
      const seasonMatches = m.season === selectedSeason;
      const selectedPlayersInMatch = selectedPlayers.find(p => allMatchPlayerIds.includes(p.id));
      const matchTypeMatches = selectedMatchTypes.includes(m.type);
      return seasonMatches && selectedPlayersInMatch && matchTypeMatches;
    });
    setAvailableMatches(availableMatches);
    setSelectedMatches(availableMatches); // I think this is the best user experience, you expect all of the matches to be selected when you change a higher level filter
  }, [allMatches, allPoints, allPartnerships, selectedSeason, selectedPlayers, selectedMatchTypes]);

  useEffect(() => {
    const filteredSelectedMatches = selectedMatches.filter(m => availableMatches.find(sm => sm.id === m.id))
    if(filteredSelectedMatches.length === selectedMatches.length) return;
    setSelectedMatches(filteredSelectedMatches);
  }, [availableMatches]);

  const enrichPoints = (points: StattedPointDto[], matches: MatchInfoDto[]): EnrichedPointDto[] => {
    return points
      .map(p => {
        const match = matches.find(m => m.sets.map(s => s.id).includes(p.setId));
        return {...p, matchInfo: match};
      }).filter(p => !!p.matchInfo) as EnrichedPointDto[];
  }

  const serveReceiveLocationData = (locationIndex: number) => {
    const serveInPlayLocation = getServeInPlayLocation(locationIndex);
    const points = pointsByServeLocation.find(p => serveInPlayLocationMatches(p.location, serveInPlayLocation))?.points ?? [];
    const pointsBreakdown = getPointsBreakdown(points, selectedPlayersWithPartnershipIds);
    const stats = getDetailedStatTotals(pointsBreakdown);
    return {
      sideoutPercentage: stats.partnership.receivePointWinPercentage,
      fbSideoutPercentage: stats.individual.fbSideoutPercentage,
      hittingPercentage: stats.individual.hittingPercentage,
      terminalKills: stats.individual.totalKills,
      terminalErrors: stats.individual.totalErrors,
      aced: stats.individual.aced,
      totalPoints: points.length,
    }
  }

  const attackLocationData = (attackLocationIndex: number, attackOriginLocationVertical: AttackOriginLocationVertical) => {
    const attackOriginLocation = {horizontal: getAttackOriginLocationHorizontal(attackLocationIndex), vertical: attackOriginLocationVertical};
    const points = pointsByAttackOriginLocation.find(p => originLocationMatches(p.location, attackOriginLocation))?.points ?? [];
    const pointsBreakdown = getPointsBreakdown(points, selectedPlayersWithPartnershipIds);
    const stats = getDetailedStatTotals(pointsBreakdown);
    return {
      fbSideoutPercentage: stats.individual.fbSideoutPercentage,
      hittingPercentage: stats.individual.hittingPercentage,
      terminalKills: stats.individual.totalKills,
      terminalErrors: stats.individual.totalErrors,
      opportunities: stats.individual.sideoutOpportunities
    }
  }

  const shotLocationData = (rowIndex: number, colIndex: number) => {
    const shotLocation = getAttackDestinationLocation(rowIndex, colIndex);
    const points = getAttacksToDestinationLocation(selectedSectionsPoints, shotLocation, selectedPlayers.map(p => p.id));
    const pointsBreakdown = getPointsBreakdown(points, selectedPlayersWithPartnershipIds);
    const stats = getDetailedStatTotals(pointsBreakdown);
    const swings = points.filter(p => p.attackInfo && isAttackWithAttackType(p.attackInfo) && p.attackInfo.attackType === AttackType.Swing);
    const shots = points.filter(p => p.attackInfo && isAttackWithAttackType(p.attackInfo) && p.attackInfo.attackType === AttackType.Shot);
    return {
      fbSideoutPercentage: stats.individual.fbSideoutPercentage,
      hittingPercentage: stats.individual.hittingPercentage,
      terminalKills: stats.individual.totalKills,
      sideoutOpportunities: stats.individual.sideoutOpportunities,
      swings: swings.length,
      shots: shots.length
    }
  }

  const attackOutOfBoundsData = (outLocation: AttackOutLocation) => {
    const points = selectedSectionsPoints.filter(p => {
      if(!p.attackInfo) return false;
      return isAttackWithOutLocation(p.attackInfo) ? p.attackInfo!.outLocation === outLocation && selectedPlayers.find(player => p.attackInfo!.attackingPlayerId == player.id) : false;
    });
    return {
      totalPoints: points.length
    }
  }

  const toPercentageString = (value: number) => isNaN(value) ? '0.000' : value.toFixed(3);

  const attackingOutOfBoundsAdditionalClasses = () => '';
  const receivingOutOfBoundsAdditionalClasses = () => '';
  const shotLocationAdditionalClasses = () => '';
  const attackLocationOrFreeAdditionalClasses = () => '';
  const receivingInBoundsAdditionalClasses = () => '';

  const attackOutOfBoundsLocationStyle = (location: AttackOutLocation) => ({backgroundColor: undefined})
  const shotLocationSectionStyle = (rowIndex: number, colIndex: number) => {
    const shotLocation = getAttackDestinationLocation(rowIndex, colIndex);
    const pointsByAllShotLocations = getAttacksByDestinationLocation(selectedSectionsPoints, selectedPlayers.map(p => p.id));
    const pointsCount = pointsByAllShotLocations.find(p => p.location === shotLocation)?.points?.length ?? 0;
    const maxPointsCount = Math.max(...pointsByAllShotLocations.map(p => p.points.length));
    const {fbSideoutPercentage} = shotLocationData(rowIndex, colIndex);
    const backgroundColor = getGradientBackgroundColor(gradientType, pointsCount, maxPointsCount, fbSideoutPercentage, 1);
    return { backgroundColor };
  }
  const attackOriginSectionStyle = (attackLocationIndex: number) => {
    const attackOriginLocation = {horizontal: getAttackOriginLocationHorizontal(attackLocationIndex), vertical: AttackOriginLocationVertical.On};
    const pointsCount = pointsByAttackOriginLocation.find(p => originLocationMatches(p.location, attackOriginLocation))?.points.length || 0;
    const maxPointsCount = Math.max(...pointsByAttackOriginLocation.map(p => p.points.length));
    const {hittingPercentage} = attackLocationData(attackLocationIndex, AttackOriginLocationVertical.On);
    const gradientBackgroundColor = getGradientBackgroundColor(gradientType, pointsCount, maxPointsCount, hittingPercentage, 1);
    const isSelected = selectedAttackOriginLocations.find(l => originLocationMatches(l, attackOriginLocation));
    return { backgroundColor: isSelected ? sectionSelectedColor : gradientBackgroundColor };
  };
  const freeBallLocationSectionStyle = () => ({backgroundColor: undefined});
  const serveReceiveSectionStyle = (locationIndex: number) => {
    const serveReceiveLocation = getServeInPlayLocation(locationIndex);
    const pointsCount = pointsByServeLocation.find(p => serveInPlayLocationMatches(p.location, serveReceiveLocation))?.points.length || 0;
    const maxPointsCount = Math.max(...pointsByServeLocation.map(p => p.points.length));
    const {sideoutPercentage} = serveReceiveLocationData(locationIndex);
    const gradientBackgroundColor = getGradientBackgroundColor(gradientType, pointsCount, maxPointsCount, sideoutPercentage, 1);
    const isSelected = selectedServeLocations.find(l => serveInPlayLocationMatches(l, serveReceiveLocation));
    return { backgroundColor: isSelected ? sectionSelectedColor : gradientBackgroundColor };
  }

  const showAttackLocations = () => analysisType === 'AttackLocation';
  const showServeReceiveLocation = () => analysisType === 'ServeLocation';

  const getServeInPlayLocation = (locationIndex: number): ServeInPlayLocation => {
    const side = locationIndex <= 2 ? CourtSide.Left : CourtSide.Right;
    if(locationIndex % 3 === 0) return ({side, playerRelativeLocation: ServePlayerRelativeLocation.Left});
    if(locationIndex % 3 === 1) return ({side, playerRelativeLocation: ServePlayerRelativeLocation.Middle});
    if(locationIndex % 3 === 2) return ({side, playerRelativeLocation: ServePlayerRelativeLocation.Right});
    throw new Error('Invalid serve in play location');
  }

  const serveReceiveLocationContent = (locationIndex: number) => {
    const {hittingPercentage, fbSideoutPercentage, terminalKills, terminalErrors, aced, totalPoints} = serveReceiveLocationData(locationIndex);
    return <Box style={{height: '100%', width: '100%', display: 'flex', alignItems: 'end', justifyContent: 'center'}}>
      <Box style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
        <Typography>{toPercentageString(fbSideoutPercentage)}</Typography>
        <Typography style={{fontSize: '12px'}}>{toPercentageString(hittingPercentage)}</Typography>
        <Typography style={{fontSize: '12px'}}>+{terminalKills}/{-(terminalErrors + aced)}</Typography>
        <Typography style={{fontSize: '12px'}}>{totalPoints}</Typography>
      </Box>
    </Box>;
  }

  const attackOriginContent = (attackLocationIndex: number) => {
    const {hittingPercentage, terminalKills, terminalErrors, opportunities} = attackLocationData(attackLocationIndex, AttackOriginLocationVertical.On);
    return <Box style={{height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column'}}>
      <Typography>{toPercentageString(hittingPercentage)}</Typography>
      <Typography style={{fontSize: '12px'}}>+{terminalKills}/{-terminalErrors}</Typography>
      <Typography style={{fontSize: '12px'}}>{opportunities}</Typography>
    </Box>;
  }

  const attackOffLocationContent = (attackLocationIndex: number) => {
    const {hittingPercentage, terminalKills, terminalErrors, opportunities} = attackLocationData(attackLocationIndex, AttackOriginLocationVertical.Off);
    return <Box style={{height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column'}}>
      <Typography>{toPercentageString(hittingPercentage)}</Typography>
      <Typography style={{fontSize: '12px'}}>+{terminalKills}/{-terminalErrors}</Typography>
      <Typography style={{fontSize: '12px'}}>{opportunities}</Typography>
    </Box>;
  }

  const attackDestinationSectionContent = (rowIndex: number, colIndex: number) => {
    const {hittingPercentage, terminalKills, swings, shots, sideoutOpportunities} = shotLocationData(rowIndex, colIndex);
    return <Box style={{height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column'}}>
      <Typography>{toPercentageString(hittingPercentage)}</Typography>
      <Typography style={{fontSize: '12px'}}>{terminalKills}/{sideoutOpportunities}</Typography>
      <Typography style={{fontSize: '12px'}}>({swings}-{shots})</Typography>
    </Box>;
  }

  const attackOutOfBoundsSectionContent = (outLocation: AttackOutLocation) => {
    const {totalPoints} = attackOutOfBoundsData(outLocation);
    return (
    <Box style={{height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column'}}>
      <Typography>{totalPoints}</Typography>
    </Box>);
  }

  const handleAttackOutError = (location: AttackOutLocation) => {
    console.log('Attack out error', location);
  }

  const handleOutOfBoundsServe = (location: ServeOutLocation) => {
    console.log('Out of bounds serve', location);
  }

  const handleShotLocationSectionClick = (rowIndex: number, colIndex: number) => {
    console.log('Shot location', rowIndex, colIndex);
  }

  const handleSchoolChange = (school: string) => {
    setSelectedSchool(school);
  }

  const handleAccordionChange = (panel: 'season' | 'players' | 'matchType' | 'matches') => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : null);
  }

  const handleSeasonChange = (event: React.ChangeEvent<HTMLInputElement>, season: Season) => {
    if(event.target.checked) {
      setSelectedSeason(season);
    }
  }

  const allPlayersSelected = () => selectedPlayers.length === selectedSchoolPlayers.length;
  const handlePlayersSelectAllCheckBoxClicked = () => {
    if(selectedPlayers.length === selectedSchoolPlayers.length) {
      setSelectedPlayers([]);
    } else {
      setSelectedPlayers(selectedSchoolPlayers);
    }
  }

  const playerIsSelected = (playerId: number) => !!selectedPlayers.find(p => p.id === playerId)
  const handlePlayerCheckBoxClicked = (playerId: number) => {
    const isSelected = selectedPlayers.find(p => p.id === playerId);
    if(isSelected) {
      setSelectedPlayers(selectedPlayers.filter(p => p.id !== playerId));
    } else {
      setSelectedPlayers([...selectedPlayers, allPlayers.find(p => p.id === playerId)!]);
    }
  }

  const allMatchTypesSelected = () => selectedMatchTypes.length === allMatchTypes.length;
  const handleMatchTypesSelectAllCheckBoxClicked = () => {
    if(selectedMatchTypes.length === allMatchTypes.length) {
      setSelectedMatchTypes([]);
    } else {
      setSelectedMatchTypes(allMatchTypes);
    }
  }

  const matchTypeIsSelected = (matchType: MatchType) => selectedMatchTypes.includes(matchType);
  const handleMatchTypeCheckBoxClicked = (matchType: MatchType) => {
    const isSelected = selectedMatchTypes.includes(matchType);
    if(isSelected) {
      setSelectedMatchTypes(selectedMatchTypes.filter(mt => mt !== matchType));
    } else {
      setSelectedMatchTypes([...selectedMatchTypes, matchType]);
    }
  }

  const allMatchesSelected = () => selectedMatches.length === availableMatches.length;
  const handleMatchSelectAllCheckBoxClicked = () => {
    if(selectedMatches.length === availableMatches.length) {
      setSelectedMatches([]);
    } else {
      setSelectedMatches(availableMatches);
    }
  }

  const matchIsSelected = (matchId: number) => !!selectedMatches.find(m => m.id === matchId);
  const handleMatchCheckBoxClicked = (matchId: number) => {
    const isSelected = selectedMatches.find(m => m.id === matchId);
    if(isSelected) {
      setSelectedMatches(selectedMatches.filter(m => m.id !== matchId));
    } else {
      setSelectedMatches([...selectedMatches, allMatches.find(m => m.id === matchId)!]);
    }
  }
  const formatMatchAsLabel = (match: MatchInfoDto) => {
    const date = new Date(match.date).toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
    let description = '';
    if(match.type === 'Tournament') {
      const homeRepresentativePlayer = allPlayers.find(ap => match.homePartnership.playerOne.id === ap.id);
      const awayRepresentativePlayer = allPlayers.find(ap => match.awayPartnership.playerOne.id === ap.id);
      description = `${homeRepresentativePlayer?.school} - ${awayRepresentativePlayer?.school}`;
    } else {
      description = match.type;
    }
    return `${date} (${description})`;
  }

  const handleAttackOriginClick = (locationIndex: number) => {
    const attackOriginLocation = {horizontal: getAttackOriginLocationHorizontal(locationIndex), vertical: AttackOriginLocationVertical.On};
    const isSelected = selectedAttackOriginLocations.find(l => originLocationMatches(l, attackOriginLocation));
    if(isSelected) {
      setSelectedAttackOriginLocations(selectedAttackOriginLocations.filter(l => !originLocationMatches(l, attackOriginLocation)));
    } else {
      setSelectedAttackOriginLocations([...selectedAttackOriginLocations, attackOriginLocation]);
    }
  }

  const handleFreeBallClick = () => {
    console.log('Free ball');
  }

  const handleServeReceiveSectionClick = (locationIndex: number) => {
    const serveInPlayLocation = getServeInPlayLocation(locationIndex);
    const isSelected = selectedServeLocations.find(l => serveInPlayLocationMatches(l, serveInPlayLocation));
    if(isSelected) {
      setSelectedServeLocations(selectedServeLocations.filter(l => !serveInPlayLocationMatches(l, serveInPlayLocation)));
    } else {
      setSelectedServeLocations([...selectedServeLocations, serveInPlayLocation]);
    }
  };

  const handleAnalysisTypeChange = (event: React.MouseEvent<HTMLElement>, analysisType: 'AttackLocation' | 'ServeLocation') => {
    if(analysisType === null) return;
    setAnalysisType(analysisType);
  }

  const handleGradientTypeChange = (event: React.MouseEvent<HTMLElement>, gradientType: 'Volume' | 'Efficiency' | 'Off') => {
    if(gradientType === null) return;
    setGradientType(gradientType);
  }

  const handlePassTypeFilterChange = (event: React.MouseEvent<HTMLElement>, passTypeFilter: PassType | 'All') => {
    console.log('Pass type filter', passTypeFilter);
    if(passTypeFilter === null) return;
    setPassTypeFilter(passTypeFilter);
  }

  const courtProps: CourtProps = {
    sectionAdditionalClasses: {
      receivingOutOfBounds: receivingOutOfBoundsAdditionalClasses(),
      receivingInBounds: receivingInBoundsAdditionalClasses(),
      attackLocationOrFree: attackLocationOrFreeAdditionalClasses(),
      shotLocation: shotLocationAdditionalClasses(),
      attackingOutOfBounds: attackingOutOfBoundsAdditionalClasses()
    },
    locationDisplaySettings: {
      showServeReceiveLocation: showServeReceiveLocation(),
      showAttackLocations: showAttackLocations(),
    },
    locationConditionalStyles: {
      attackDestinationSection: shotLocationSectionStyle,
      attackOriginSection: attackOriginSectionStyle,
      freeBallLocationSection: freeBallLocationSectionStyle,
      serveReceiveSection: serveReceiveSectionStyle,
      attackOutOfBoundsLocation: attackOutOfBoundsLocationStyle
    },
    clickHandlers: {
      handleServeReceiveSectionClick,
      handleAttackOriginClick,
      handleAttackDestinationSectionClick: handleShotLocationSectionClick,
      handleAttackOutClick: handleAttackOutError,
      handleOutOfBoundsServeClick: handleOutOfBoundsServe,
      handleFreeBallClick
    },
    conditionalContent: {
      serveReceiveLocation: serveReceiveLocationContent,
      attackOrigin: attackOriginContent,
      attackDestination: attackDestinationSectionContent,
      attackOutOfBounds: attackOutOfBoundsSectionContent
    }
  }

  return (
    <div className={'page-background'}>
      {isLoading && <LinearProgress />}
      <Box className={`page-content-container`}>
        <Box className={`player-and-court-container`}>
          <Box style={{display: 'flex', flexDirection: 'column', gap: '10px', width: '300px', margin: '15px'}}>
            <FormControl fullWidth>
              <Autocomplete
                options={schools}
                getOptionLabel={(school: string) =>school}
                renderInput={(params) => <TextField {...params} label="School" />}
                value={selectedSchool}
                onChange={(event, newValue) => handleSchoolChange(newValue!)}
              />
            </FormControl>
          </Box>
          <DetailedStatsCard detailedStats={detailedPlayerStats}/>
          <Box style={{display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '20px', width: '100%'}}>
            <Box style={{display: 'flex', flexDirection: 'column', width: '250px'}}>
              <Box
                component="fieldset"
                sx={{border: '1px solid', borderColor: theme.palette.divider, borderRadius: '8px', padding: '4px', position: 'relative', marginTop: '24px'}}
              >
                <Typography component="legend" sx={{position: 'absolute', top: '-13px', left: '16px', fontSize: '16px', backgroundColor: theme.palette.background.default}}>
                  Quick Filters
                </Typography>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginTop: '15px'}}>
                  <Typography variant={'body2'} style={{fontSize: '12px'}}>Pass Type</Typography>
                  <ToggleButtonGroup style={{marginLeft: '10px', marginRight: '10px'}} color="primary" value={passTypeFilter} exclusive onChange={handlePassTypeFilterChange}>
                    <ToggleButton value="All" style={{fontSize: '12px'}}>All</ToggleButton>
                    <ToggleButton value={PassType.InSystem} style={{fontSize: '12px'}}>I/S</ToggleButton>
                    <ToggleButton value={PassType.OutOfSystem} style={{fontSize: '12px'}}>O/S</ToggleButton>
                  </ToggleButtonGroup>
                </div>
              </Box>
              <Box
                component="fieldset"
                sx={{border: '1px solid', borderColor: theme.palette.divider, borderRadius: '8px', padding: '4px', position: 'relative', marginTop: '24px'}}
              >
                <Typography component="legend" sx={{position: 'absolute', top: '-13px', left: '16px', fontSize: '16px', backgroundColor: theme.palette.background.default}}>
                  Court Settings
                </Typography>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginTop: '15px'}}>
                  <Typography variant={'body2'} style={{fontSize: '12px'}}>Gradient</Typography>
                  <ToggleButtonGroup style={{marginLeft: '10px', marginRight: '10px'}} color="primary" value={gradientType} exclusive onChange={handleGradientTypeChange}>
                    <ToggleButton value="Volume" style={{fontSize: '12px'}}>Volume</ToggleButton>
                    <ToggleButton value="Efficiency" style={{fontSize: '12px'}}>Efficiency</ToggleButton>
                    <ToggleButton value="Off" style={{fontSize: '12px'}}>Off</ToggleButton>
                  </ToggleButtonGroup>
                </div>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginTop: '5px'}}>
                  <Typography variant={'body2'} style={{fontSize: '12px'}}>Locations</Typography>
                  <ToggleButtonGroup style={{marginLeft: '10px', marginRight: '10px'}} color="primary" value={analysisType} exclusive onChange={handleAnalysisTypeChange}>
                    <ToggleButton value='AttackLocation' style={{fontSize: '12px'}}>Attacks</ToggleButton>
                    <ToggleButton value='ServeLocation' style={{fontSize: '12px'}}>Receptions</ToggleButton>
                  </ToggleButtonGroup>
                </div>
              </Box>
              <Box
                component="fieldset"
                sx={{border: '1px solid', borderColor: theme.palette.divider, borderRadius: '8px', padding: '4px', position: 'relative', marginTop: '24px', maxHeight: '490px'}}
              >
                <Typography component="legend" sx={{position: 'absolute', top: '-13px', left: '16px', fontSize: '16px', backgroundColor: theme.palette.background.default}}>
                  Filters
                </Typography>
                <Accordion expanded={expandedAccordion === 'season'} onChange={handleAccordionChange('season')} style={{marginTop: '10px', marginBottom: '10px', maxHeight: '250px', overflowY: 'auto'}}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Season</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormControl component="fieldset">
                      <FormGroup>
                        {allSeasons.map((season) => (
                            <FormControlLabel
                                sx={{'& .MuiFormControlLabel-label': {fontSize: '14px'}}}
                                key={season}
                                control={<Radio checked={selectedSeason === season} onChange={e => handleSeasonChange(e, season)} name={season}/>}
                                label={season}
                            />
                        ))}
                      </FormGroup>
                    </FormControl>
                  </AccordionDetails>
                </Accordion>
                <Accordion expanded={expandedAccordion === 'players'} onChange={handleAccordionChange('players')} style={{marginTop: '10px', marginBottom: '10px', maxHeight: '250px', overflowY: 'auto'}}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Players</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormControl component="fieldset">
                      <FormGroup>
                        <FormControlLabel
                          sx={{'& .MuiFormControlLabel-label': {fontSize: '14px'}}}
                          control={<Checkbox checked={allPlayersSelected()} onChange={() => handlePlayersSelectAllCheckBoxClicked()} name="all" />}
                          label="All"
                        />
                        {selectedSchoolPlayers.map((player) => (
                          <FormControlLabel
                            sx={{'& .MuiFormControlLabel-label': {fontSize: '14px'}}}
                            key={player.id}
                            control={<Checkbox checked={playerIsSelected(player.id)} onChange={() => handlePlayerCheckBoxClicked(player.id)} name={player.lastName} />}
                            label={`${player.firstName.slice(0, 1)}. ${player.lastName}`}
                          />
                        ))}
                      </FormGroup>
                    </FormControl>
                  </AccordionDetails>
                </Accordion>
                <Accordion expanded={expandedAccordion === 'matchType'} onChange={handleAccordionChange('matchType')} style={{marginTop: '10px', marginBottom: '10px', maxHeight: '250px', overflowY: 'auto'}}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Match Type</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormControl component="fieldset">
                      <FormGroup>
                        <FormControlLabel
                          sx={{'& .MuiFormControlLabel-label': {fontSize: '14px'}}}
                          control={<Checkbox checked={allMatchTypesSelected()} onChange={() => handleMatchTypesSelectAllCheckBoxClicked()} name="all" />}
                          label="All"
                        />
                        {allMatchTypes.map((matchType) => (
                          <FormControlLabel
                            sx={{'& .MuiFormControlLabel-label': {fontSize: '14px'}}}
                            key={matchType}
                            control={<Checkbox checked={matchTypeIsSelected(matchType)} onChange={() => handleMatchTypeCheckBoxClicked(matchType)} name={matchType} />}
                            label={matchType}
                          />
                        ))}
                      </FormGroup>
                    </FormControl>
                  </AccordionDetails>
                </Accordion>
                <Accordion expanded={expandedAccordion === 'matches'} onChange={handleAccordionChange('matches')} style={{marginTop: '10px', marginBottom: '10px', maxHeight: '250px', overflowY: 'auto'}}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Matches</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormControl component="fieldset">
                      <FormGroup>
                        <FormControlLabel
                            sx={{'& .MuiFormControlLabel-label': {fontSize: '14px'}}}
                            control={<Checkbox checked={allMatchesSelected()} onChange={() => handleMatchSelectAllCheckBoxClicked()} name="all" />}
                            label="All"
                        />
                        {availableMatches.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map((match) => (
                            <FormControlLabel
                                sx={{'& .MuiFormControlLabel-label': {fontSize: '14px'}}}
                                key={match.id}
                                control={<Checkbox checked={matchIsSelected(match.id)} onChange={() => handleMatchCheckBoxClicked(match.id)} name={match.date} />}
                                label={formatMatchAsLabel(match)}
                            />
                        ))}
                      </FormGroup>
                    </FormControl>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Box>
            <Box style={{width: '350px'}}>
              <Court
                isRotated={courtProps.isRotated}
                sectionAdditionalClasses={courtProps.sectionAdditionalClasses}
                locationDisplaySettings={courtProps.locationDisplaySettings}
                locationConditionalStyles={courtProps.locationConditionalStyles}
                clickHandlers={courtProps.clickHandlers}
                conditionalContent={courtProps.conditionalContent}
              ></Court>
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  );
}

export default AnalysisPage;
